import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, FormGroup } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useDispatch, useSelector } from 'react-redux';
import { MultiSelect } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';
import { createOptions, checkArray } from '../../../../../Components/helper';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';
import * as commonAction from '../../TodoCommon.action';

function SimpleFilter({ applyFilter, onSecondaryAction, supplierList = [], colourwayList = [], statusList = [], filterState = {}, setFilterState }) {
  const dispatch = useDispatch();
  const { t } = useTranslation('purchase-order');
  const reducer = useSelector((reducerState) => reducerState);
  const { UserDetails } = reducer;
  const { isSupplierLoggedIn, isCompanyUserLoggedIn } = UserDetails;

  const [isApply, setIsApply] = useState(false);
  const [userSuppliers, setUserSuppliers] = useState([]);

  const { activeSupplier = [], activeColourways = [], activeStatus = [] } = filterState;

  const handleFilterApply = () => {
    const filterObj = {
      supplierId: activeSupplier,
      colourwayId: activeColourways,
      status: activeStatus,
    };

    setIsApply(true);
    applyFilter(filterObj);
  };

  const handleFilterClear = () => {
    setIsApply(false);
    const filterObj = {
      activeSupplier: [],
      activeColourways: [],
      activeStatus: [],
    };
    setFilterState((prevState) => ({
      ...prevState,
      ...filterObj,
    }));
    applyFilter({
      supplierId: [],
      colourwayId: [],
      status: [],
    });
  };

  const handleFilterSelect = (val, key) => {
    setFilterState((prevState) => ({
      ...prevState,
      [key]: val,
    }));
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    }
  };

  useEffect(() => {
    const callFn = async () => {
      if (isCompanyUserLoggedIn) {
        const data = await dispatch(commonAction.getUserSuppliers());
        const supplist = data?.suppliers?.length
          ? data?.suppliers?.map((ele) => {
              const supplier = { ...ele };
              if (supplier.companies.length) {
                supplier.name = supplier.companies[0].supplierName;
              }
              return { _id: supplier._id, name: supplier.name };
            })
          : [];
        setUserSuppliers(supplist);
      }
    };
    callFn();
  }, [isCompanyUserLoggedIn, dispatch]);

  const supplierOptions = useMemo(() => {
    if (checkArray(supplierList) && !isCompanyUserLoggedIn) {
      return createOptions(supplierList, 'name', '_id');
    }
    if (checkArray(userSuppliers) && isCompanyUserLoggedIn) {
      return createOptions(userSuppliers, 'name', '_id');
    }
    return [];
  }, [supplierList, isCompanyUserLoggedIn, userSuppliers]);

  const colourwayOptions = useMemo(() => {
    if (checkArray(colourwayList)) return createOptions(colourwayList, 'name', '_id');
    return [];
  }, [colourwayList]);

  const statusOptions = useMemo(() => {
    if (checkArray(statusList)) return createOptions(statusList, 'name', '_id');
    return [];
  }, [statusList]);

  return (
    <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.FILTER')}>
      <button type="button" aria-label={t('COMMON_LABEL.FILTER')} className={isApply ? 'btn d-inline-flex active' : 'btn d-inline-flex'}>
        <OverlayTrigger
          placement="bottom"
          trigger="click"
          rootClose
          overlay={
            <Popover className="custom-popover popover-filter">
              <Popover.Content>
                <Row className="form-row">
                  {!isSupplierLoggedIn && (
                    <Col md={4}>
                      <FormGroup>
                        <MultiSelect
                          name="supplier"
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={supplierOptions?.filter((val) => checkArray(supplierOptions) && activeSupplier.includes(val.value))}
                          labelledBy="Select Supplier"
                          overrideStrings={{
                            selectSomeItems: 'Select Supplier',
                          }}
                          onChange={(selected) =>
                            handleFilterSelect(
                              selected.map((val) => val.value),
                              'activeSupplier',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}

                  <Col md={4}>
                    <FormGroup>
                      <MultiSelect
                        name="colourways"
                        options={colourwayOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={colourwayOptions?.filter((val) => checkArray(colourwayOptions) && activeColourways.includes(val.value))}
                        labelledBy="Select Colourways"
                        overrideStrings={{
                          selectSomeItems: 'Select Colourways',
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeColourways',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <MultiSelect
                        name="status"
                        options={statusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={statusOptions?.filter((val) => checkArray(statusOptions) && activeStatus.includes(val.value))}
                        labelledBy="Select Status"
                        overrideStrings={{
                          selectSomeItems: 'Select Status',
                        }}
                        onChange={(selected) =>
                          handleFilterSelect(
                            selected.map((val) => val.value),
                            'activeStatus',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <div className="d-flex justify-content-end">
                      <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.CLEAR')}>
                        <button onClick={() => handleFilterClear()} aria-label="Clear" className="btn p-0 d-inline-flex" type="button">
                          <span className="icon-Clear-New f-24" />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.APPLY')}>
                        <button onClick={() => handleFilterApply()} aria-label="Apply" className="btn p-0 d-inline-flex ml-16" type="button">
                          <span className="icon-Tick-1 f-28" />
                        </button>
                      </CustomTooltip>
                      {onSecondaryAction && (
                        <CustomTooltip placement="top" tooltipText="Additional Actions">
                          <button onClick={() => handleSecondaryAction()} aria-label="Additional Actions" className="btn p-0 d-inline-flex ml-16" type="button">
                            <span className="icon-Document f-24" />
                          </button>
                        </CustomTooltip>
                      )}
                    </div>
                  </Col>
                </Row>
              </Popover.Content>
            </Popover>
          }
        >
          <i className="icon-Filter f-26" />
        </OverlayTrigger>
      </button>
    </CustomTooltip>
  );
}

export default SimpleFilter;
